// InvoiceParseReview.tsx
// Post-parse human review, split-screen:
//   left  — the invoice PDF (source of truth, big)
//   right — the parsed line items (editable), Confirm & Save
// The user compares every parsed value against the PDF before it becomes the
// bill's "actual" side — same human-in-the-loop gate as the assign popup.

import { useState, useEffect } from 'react';
import { Loader2, AlertCircle, FileText, Download } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { attachmentService } from '../../services/attachmentService';
import { isRenderableFilename } from '../../utils/attachmentPreview';
import { ParsedInvoiceEditor } from './ParsedInvoiceEditor';
import type { BvBill, ActualBillResult } from '../../types/billTypes';
import { effectiveInvoice } from '../../lib/effectiveInvoice';

type Props = {
  open: boolean;
  bill: BvBill | null;
  onSave: (result: ActualBillResult, note: string) => Promise<void>;
  /** Drops every hand correction and returns to what the parser read. */
  onRevert?: () => Promise<void>;
  onClose: () => void;
};

export function InvoiceParseReview({ open, bill, onSave, onRevert, onClose }: Props) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !bill?.invoice_path) { setPdfUrl(null); return; }
    let mounted = true;
    setPdfLoading(true);
    setPdfError(null);
    attachmentService.getSignedUrl(bill.invoice_path, 3600)
      .then(url => { if (mounted) setPdfUrl(url); })
      .catch(err => { if (mounted) setPdfError(err instanceof Error ? err.message : 'Failed to load PDF'); })
      .finally(() => { if (mounted) setPdfLoading(false); });
    return () => { mounted = false; };
  }, [open, bill?.invoice_path]);

  const effective = effectiveInvoice(bill);
  if (!bill?.actual_parsed_result) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="text-base">
            Review parsed invoice
            <span className="ml-2 text-xs font-normal text-gray-500">
              {new Date(bill.bill_from).toLocaleDateString('en-AU')} – {new Date(bill.bill_to).toLocaleDateString('en-AU')} ·
              compare each value against the PDF, correct if needed, then Save
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex min-h-0">
          {/* Left: the invoice PDF */}
          <div className="flex-1 min-w-0 border-r border-gray-200 bg-gray-100 flex items-center justify-center">
            {!bill.invoice_path ? (
              <p className="text-sm text-gray-400">No invoice PDF on this bill</p>
            ) : pdfLoading ? (
              <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
            ) : pdfError ? (
              <p className="flex items-center gap-2 text-sm text-red-600 px-4 text-center">
                <AlertCircle className="h-4 w-4 shrink-0" /> {pdfError}
              </p>
            ) : pdfUrl && isRenderableFilename(bill.invoice_path) ? (
              <iframe src={pdfUrl} title="Invoice PDF" className="w-full h-full" />
            ) : pdfUrl ? (
              // Framing a stored Word/Excel file downloads it instead of showing
              // it — job 18's defect, same cause on this screen.
              <div className="flex flex-col items-center gap-3 px-6 text-center">
                <FileText className="h-10 w-10 text-gray-300" />
                <p className="text-sm font-medium text-gray-700">
                  This file can't be previewed in the browser
                </p>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-blue-600 hover:bg-gray-50"
                >
                  <Download className="h-4 w-4" /> Open the invoice file
                </a>
              </div>
            ) : null}
          </div>

          {/* Right: editable parsed values */}
          <div className="w-[620px] shrink-0 overflow-y-auto p-4">
            {/* Opens on the CORRECTED figures where they exist, with the parse
                passed alongside so each changed field can show what it was. */}
            <ParsedInvoiceEditor
              initialResult={effective.result ?? bill.actual_parsed_result}
              parsedResult={effective.parsed}
              isEdited={effective.source === 'edited'}
              onSave={onSave}
              onRevert={onRevert}
              onCancel={onClose}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
